import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  XMarkIcon,
  ShieldCheckIcon,
  ChartBarIcon,
  CreditCardIcon,
  AdjustmentsHorizontalIcon,
} from "@heroicons/react/24/outline";
import { cookieManager } from "utils/cookieManager";

interface CookiePreferences {
  necessary: boolean;
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
}

const COOKIE_CONSENT_KEY = "eventToBee_cookieConsent";
const COOKIE_PREFERENCES_KEY = "eventToBee_cookiePreferences";

const CookieConsent: React.FC = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true, // Toujours requis
    functional: false,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    // Vérifier si l'utilisateur a déjà donné son consentement
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    const savedPreferences = localStorage.getItem(COOKIE_PREFERENCES_KEY);

    if (!consent) {
      // Attendre un délai avant d'afficher la bannière
      const timer = setTimeout(() => setShowBanner(true), 2000);
      return () => clearTimeout(timer);
    } else if (savedPreferences) {
      try {
        const parsedPreferences = JSON.parse(savedPreferences);
        setPreferences(parsedPreferences);
        applyCookieSettings(parsedPreferences);
      } catch (error) {
        console.error("Error parsing cookie preferences:", error);
      }
    }
  }, []);

  const applyCookieSettings = (prefs: CookiePreferences) => {
    // Sauvegarder dans cookieManager et appliquer les paramètres
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(prefs));
    cookieManager.refreshPreferences();

    // Initialiser ou désactiver les services selon les préférences
    if (prefs.analytics) {
      cookieManager.initializeAnalytics();
    } else {
      console.log("Analytics cookies disabled");
    }

    if (prefs.marketing) {
      cookieManager.initializeMarketing();
    } else {
      console.log("Marketing cookies disabled");
    }

    if (!prefs.functional) {
      console.log("Non-essential functional cookies disabled");
    }

    // Nettoyer les cookies désactivés
    cookieManager.cleanupDisabledCookies();
  };

  const handleAcceptAll = () => {
    const allAccepted: CookiePreferences = {
      necessary: true,
      functional: true,
      analytics: true,
      marketing: true,
    };

    saveConsent(allAccepted);
    applyCookieSettings(allAccepted);
    setShowBanner(false);
  };

  const handleRejectAll = () => {
    const onlyNecessary: CookiePreferences = {
      necessary: true,
      functional: false,
      analytics: false,
      marketing: false,
    };

    saveConsent(onlyNecessary);
    applyCookieSettings(onlyNecessary);
    setShowBanner(false);
  };

  const handleSavePreferences = () => {
    saveConsent(preferences);
    applyCookieSettings(preferences);
    setShowBanner(false);
  };

  const saveConsent = (prefs: CookiePreferences) => {
    localStorage.setItem(
      COOKIE_CONSENT_KEY,
      JSON.stringify({
        timestamp: new Date().toISOString(),
        version: "1.0",
      })
    );
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(prefs));
    setPreferences(prefs);
  };

  const togglePreference = (key: keyof CookiePreferences) => {
    if (key === "necessary") return; // Les cookies nécessaires ne peuvent pas être désactivés

    setPreferences((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const cookieTypes = [
    {
      key: "necessary" as const,
      name: "Cookies nécessaires",
      description:
        "Ces cookies sont essentiels pour le fonctionnement du site. Ils permettent l'authentification, la sécurité et les fonctionnalités de base.",
      icon: ShieldCheckIcon,
      examples: ["Token d'authentification", "Préférences de sécurité", "Sessions utilisateur"],
      required: true,
    },
    {
      key: "functional" as const,
      name: "Cookies fonctionnels",
      description: "Ces cookies améliorent votre expérience en mémorisant vos préférences (thème, langue, etc.).",
      icon: ShieldCheckIcon,
      examples: ["Préférences de thème", "Paramètres d'affichage", "Préférences linguistiques"],
      required: false,
    },
    {
      key: "analytics" as const,
      name: "Cookies d'analyse",
      description: "Ces cookies nous aident à comprendre comment vous utilisez notre site pour l'améliorer.",
      icon: ChartBarIcon,
      examples: ["Google Analytics", "Statistiques d'usage", "Mesure de performance"],
      required: false,
    },
    {
      key: "marketing" as const,
      name: "Cookies marketing",
      description:
        "Ces cookies sont utilisés pour afficher des publicités pertinentes et mesurer l'efficacité des campagnes.",
      icon: CreditCardIcon,
      examples: ["Stripe Analytics", "Publicités ciblées", "Suivi des conversions"],
      required: false,
    },
  ];

  return (
    <AnimatePresence>
      {showBanner && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed bottom-0 left-0 right-0 z-50 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 shadow-2xl"
        >
          <div className="max-w-7xl mx-auto p-6">
            {!showDetails ? (
              // Bannière simple
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                <div className="flex items-start gap-4 flex-1">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-2xl">🍪</span>
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white">Gestion des cookies</h3>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                      Event To Bee utilise des cookies essentiels pour le fonctionnement du site, ainsi que des
                      cookies optionnels pour améliorer votre expérience, analyser l'utilisation et personnaliser
                      le contenu. Conformément au RGPD, vous avez le contrôle total sur vos données.
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                      En savoir plus :{" "}
                      <a href="/cookies" className="text-blue-600 hover:underline">
                        Politique des cookies
                      </a>{" "}
                      |
                      <a href="/privacy" className="text-blue-600 hover:underline ml-1">
                        Confidentialité
                      </a>
                    </p>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
                  <button
                    onClick={() => setShowDetails(true)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors flex items-center gap-2"
                  >
                    <AdjustmentsHorizontalIcon className="w-4 h-4" />
                    Personnaliser
                  </button>
                  <button
                    onClick={handleRejectAll}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  >
                    Refuser tout
                  </button>
                  <button
                    onClick={handleAcceptAll}
                    className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Accepter tout
                  </button>
                </div>
              </div>
            ) : (
              // Vue détaillée des préférences
              <div className="max-h-96 overflow-y-auto">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">🍪</span>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                      Préférences détaillées des cookies
                    </h3>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                    Gérez vos préférences de cookies en toute transparence. Conforme RGPD.
                  </p>
                  <button
                    onClick={() => setShowDetails(false)}
                    className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  >
                    <XMarkIcon className="w-5 h-5" />
                  </button>
                </div>

                <div className="grid gap-4 mb-6">
                  {cookieTypes.map((type) => {
                    const Icon = type.icon;
                    return (
                      <div key={type.key} className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <Icon className="w-5 h-5 text-blue-600" />
                            <div>
                              <h4 className="font-semibold text-gray-900 dark:text-white">{type.name}</h4>
                              {type.required && (
                                <span className="text-xs bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 px-2 py-1 rounded-full mt-1 inline-block">
                                  Requis
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              id={type.key}
                              checked={preferences[type.key]}
                              onChange={() => togglePreference(type.key)}
                              disabled={type.required}
                              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 disabled:opacity-50"
                            />
                            <label htmlFor={type.key} className="sr-only">
                              {type.name}
                            </label>
                          </div>
                        </div>

                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{type.description}</p>

                        <div className="text-xs text-gray-500 dark:text-gray-500">
                          <span className="font-medium">Exemples :</span> {type.examples.join(", ")}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="flex flex-col sm:flex-row gap-3 justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
                  <button
                    onClick={handleRejectAll}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  >
                    Refuser tout
                  </button>
                  <button
                    onClick={handleSavePreferences}
                    className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Enregistrer les préférences
                  </button>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieConsent;
